// ---------------------------------------------------------------------------
// The site-level data every "Stock from CAD" consumer needs, resolved once:
// the CAD take-off, the site's live inventory, the area catalog, the
// storey → floor mapping, and the working site's tz. `useTaskStock` (one
// task's own claims) and `useStockFlags` (every task's claims, for the Gantt
// row flag) both build on this — extracted so the three queries + the
// storey/floor mapping aren't duplicated between them.
//
// tz: `selectSiteTz` is `undefined` until the working site resolves (frontend
// CLAUDE.md invariant #6 — never default it blindly). Rather than typing `tz`
// as optional and pushing that check onto every consumer, the unresolved case
// is folded into `ready`: callers render/compute nothing that reads `tz`
// until `ready` is true, so the placeholder `''` returned in the meantime is
// never actually used for date math.
// ---------------------------------------------------------------------------

import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import type { ISiteLocations } from '@interfaces/IScheduler';
import type { ICadTakeoffResponse } from '@interfaces/ICadTakeoff';
import type { Delivery, UntrackedMaterial, InventoryLevel } from '@interfaces/ILogistics';
import { fetchCadTakeoff, CadModelUnavailableError } from '@apis/cad';
import { fetchSiteLogistics } from '@apis/logistics';
import { cadTakeoffKey } from '@constants/queryKeys';
import { useSiteLocations } from '@hooks/useSiteLocations';
import { selectSiteTz } from '@store/selectors/site.selectors';
import { mapStoreysToFloors } from './storeyFloorMap';
import { openOrderFor } from './joinOrder';

export interface ISiteStockContext {
  /** True once the take-off, the site logistics and the working site's tz
   *  have all settled (success or error) — see the tz note above. */
  ready: boolean;
  /** The raw take-off response, or `undefined` while pending / on a fetch
   *  error — callers fall back to an empty materials list in that case. */
  takeoff: ICadTakeoffResponse | undefined;
  /** The server answered "there is no take-off here" (`CadModelUnavailableError`
   *  — the site has no active/processed CAD model, as every CAD version
   *  converted before this feature shipped has none). Distinct from "settled
   *  with an empty take-off", which is a real, if empty, model: the UI says
   *  "re-convert a CAD version" only for this case, and never mistakes it for
   *  "the CAD holds no materials". */
  takeoffMissing: boolean;
  /** The take-off request failed for ANY other reason — offline, a 500, a
   *  malformed body. Kept apart from `takeoffMissing` because the advice
   *  differs: nothing about the CAD is wrong, so telling the user to re-convert
   *  it would send them off to fix a model that is fine. */
  takeoffFailed: boolean;
  cadVersionId: string | null;
  inventory: InventoryLevel[];
  /** The site's delivery orders — a claim is "settled" once one of them is
   *  linked to its task and material for at least the claim's need (see
   *  `useTaskStock.bookingFor`). */
  deliveries: Delivery[];
  /** Materials that exist on the delivery side only — what Inventory Levels
   *  shows above its tracked rows. The Stock tab shows the same row for a
   *  claim whose material has an order but no stock yet. */
  untracked: UntrackedMaterial[];
  /** The site's open delivery order for a material, whoever raised it
   *  (`openOrderFor`) — what a booking with no stock on site may join
   *  instead of asking for a second order. */
  openOrderFor(materialName: string): Delivery | null;
  catalog: ISiteLocations;
  /** Take-off storey `localId` → site floor `id`, empty until the take-off
   *  resolves (see `mapStoreysToFloors`). */
  mapping: Map<number, string>;
  tz: string;
}

const NO_LEVELS: InventoryLevel[] = [];
const NO_DELIVERIES: Delivery[] = [];
const NO_UNTRACKED: UntrackedMaterial[] = [];

export function useSiteStockContext(siteId: string): ISiteStockContext {
  const takeoffQuery = useQuery({
    queryKey: cadTakeoffKey(siteId),
    queryFn: ({ signal }) => fetchCadTakeoff(siteId, signal),
    staleTime: Infinity,
    retry: false,
  });
  const catalog = useSiteLocations(siteId);
  // Same cache entry the Logistics page uses (`useLogisticsQueries`) — every
  // stock consumer reads the site's live levels but never writes them, so
  // sharing the key is enough; no separate invalidation wiring needed here.
  const logisticsQuery = useQuery({
    queryKey: ['logistics', siteId],
    queryFn: ({ signal }) => fetchSiteLogistics(siteId, signal),
  });

  const tzRaw = useSelector(selectSiteTz);
  const tz = tzRaw ?? '';

  const mapping = useMemo(
    () =>
      takeoffQuery.data
        ? mapStoreysToFloors(takeoffQuery.data.takeoff.storeys, catalog.floors)
        : new Map<number, string>(),
    [takeoffQuery.data, catalog],
  );

  const cadVersionId = takeoffQuery.data?.cadVersionId ?? null;
  // Module-level empties rather than a fresh `[]` per render: `useTaskStock`
  // memoises its resolvers on these, and a new array each render would make
  // every one of them — and the tab's `evaluate()` memo — churn while the
  // logistics query is still in flight.
  const inventory: InventoryLevel[] = logisticsQuery.data?.inventory ?? NO_LEVELS;
  const deliveries: Delivery[] = logisticsQuery.data?.deliveries ?? NO_DELIVERIES;
  const untracked: UntrackedMaterial[] = logisticsQuery.data?.untracked ?? NO_UNTRACKED;
  const openOrder = useCallback(
    (materialName: string): Delivery | null => openOrderFor(deliveries, materialName),
    [deliveries],
  );
  const ready = !takeoffQuery.isPending && !logisticsQuery.isPending && tzRaw !== undefined;
  const takeoffMissing = takeoffQuery.error instanceof CadModelUnavailableError;

  return {
    ready,
    takeoff: takeoffQuery.data,
    takeoffMissing,
    takeoffFailed: takeoffQuery.isError && !takeoffMissing,
    cadVersionId,
    inventory,
    deliveries,
    untracked,
    openOrderFor: openOrder,
    catalog,
    mapping,
    tz,
  };
}
